import { useState, useCallback, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    Search, CalendarIcon, CreditCard, User, DollarSign,
    ChevronLeft, ChevronRight, Loader2, Mail, X, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = '/api';

export default function StripeHistory() {
    const { getAuthHeaders } = useAuth();
    const [searchName, setSearchName] = useState('');
    const [searchAmount, setSearchAmount] = useState('');
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [hasSearched, setHasSearched] = useState(false);

    const fetchResults = useCallback(async (pg = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (searchName.trim()) params.append('search', searchName.trim());
            if (searchAmount.trim()) params.append('amount', searchAmount.trim());
            if (dateFrom) params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
            if (dateTo) params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
            params.append('page', pg.toString());
            params.append('limit', '10');

            const res = await axios.get(`${API}/admin/stripe-history?${params}`, {
                headers: getAuthHeaders()
            });
            setResults(res.data.results);
            setTotal(res.data.total);
            setPage(res.data.page);
            setPages(res.data.pages);
            setHasSearched(true);
        } catch (err) {
            console.error(err);
            toast.error('Error al buscar transacciones');
        } finally {
            setLoading(false);
        }
    }, [searchName, searchAmount, dateFrom, dateTo, getAuthHeaders]);

    const fetchRef = useRef(fetchResults);
    fetchRef.current = fetchResults;
    useEffect(() => { fetchRef.current(1); }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchResults(1);
    };

    const clearFilters = () => {
        setSearchName('');
        setSearchAmount('');
        setDateFrom(null);
        setDateTo(null);
        setPage(1);
        setTimeout(() => fetchRef.current(1), 0);
    };

    const hasFilters = searchName || searchAmount || dateFrom || dateTo;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="stripe-history-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    Historial Stripe
                </h1>
                <p className="text-slate-500 mt-1 text-sm">
                    Todas las transacciones de Stripe (retención 6 meses), 10 por página. Filtra por nombre, monto o fecha si lo necesitas
                </p>
            </div>

            {/* Search Form */}
            <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                    <form onSubmit={handleSearch} className="space-y-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="flex-1 relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    placeholder="Buscar por nombre..."
                                    value={searchName}
                                    onChange={(e) => setSearchName(e.target.value)}
                                    className="pl-9"
                                    data-testid="stripe-search-name"
                                />
                            </div>
                            <div className="w-full sm:w-40 relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    placeholder="Monto..."
                                    type="number"
                                    step="0.01"
                                    value={searchAmount}
                                    onChange={(e) => setSearchAmount(e.target.value)}
                                    className="pl-9"
                                    data-testid="stripe-search-amount"
                                />
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-sm"
                                        data-testid="stripe-date-from"
                                    >
                                        <CalendarIcon className="w-4 h-4 mr-1.5" />
                                        {dateFrom ? format(dateFrom, 'dd/MM/yyyy') : 'Desde'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} locale={es} />
                                </PopoverContent>
                            </Popover>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-sm"
                                        data-testid="stripe-date-to"
                                    >
                                        <CalendarIcon className="w-4 h-4 mr-1.5" />
                                        {dateTo ? format(dateTo, 'dd/MM/yyyy') : 'Hasta'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar mode="single" selected={dateTo} onSelect={setDateTo} locale={es} />
                                </PopoverContent>
                            </Popover>
                            <Button
                                type="submit"
                                size="sm"
                                disabled={loading}
                                className="bg-violet-600 hover:bg-violet-700 text-white"
                                data-testid="stripe-search-btn"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Search className="w-4 h-4 mr-1.5" />}
                                Buscar
                            </Button>
                            {hasFilters && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={clearFilters}
                                    className="text-slate-500"
                                    data-testid="stripe-clear-btn"
                                >
                                    <X className="w-4 h-4 mr-1" /> Limpiar
                                </Button>
                            )}
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* Results */}
            {loading && (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
                </div>
            )}

            {!loading && hasSearched && (
                <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
                            <Mail className="w-4 h-4 text-violet-500" />
                            {total} transacci{total === 1 ? 'ón' : 'ones'}{hasFilters ? ` encontrada${total !== 1 ? 's' : ''}` : ' en total'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {results.length === 0 ? (
                            <div className="text-center py-10 text-slate-400">
                                <Search className="w-10 h-10 mx-auto mb-3 opacity-40" />
                                <p className="font-medium">No se encontraron transacciones</p>
                                <p className="text-sm mt-1">Intenta con otros filtros</p>
                            </div>
                        ) : (
                            <>
                                <div className="divide-y divide-slate-100">
                                    {results.map((tx, i) => (
                                        <div
                                            key={tx.email_id || i}
                                            className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                                            data-testid={`stripe-result-${i}`}
                                        >
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                                                    <CreditCard className="w-4 h-4 text-violet-600" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-slate-800 truncate text-sm">
                                                        {tx.sender_name || 'Sin nombre'}
                                                    </p>
                                                    <p className="text-xs text-slate-400 truncate">
                                                        {tx.subject}
                                                    </p>
                                                    {tx.has_invoice ? (
                                                        <p
                                                            className="text-xs mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium"
                                                            data-testid={`stripe-result-user-${i}`}
                                                        >
                                                            <CheckCircle2 className="w-3 h-3" />
                                                            Confirmado por {tx.invoice_user || '—'}
                                                            {tx.invoice_name && <span className="text-emerald-600/70">· {tx.invoice_name}</span>}
                                                        </p>
                                                    ) : (
                                                        <p
                                                            className="text-xs mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-500"
                                                            data-testid={`stripe-result-user-${i}`}
                                                        >
                                                            Sin factura
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4 sm:gap-6 pl-12 sm:pl-0">
                                                <span className="text-lg font-bold text-violet-700 tabular-nums whitespace-nowrap">
                                                    ${tx.amount?.toFixed(2)}
                                                </span>
                                                <span className="text-xs text-slate-400 whitespace-nowrap">
                                                    {tx.date_str}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination */}
                                {pages > 1 && (
                                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={page <= 1}
                                            onClick={() => fetchResults(page - 1)}
                                            data-testid="stripe-prev-page"
                                        >
                                            <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                                        </Button>
                                        <span className="text-sm text-slate-500">
                                            Página {page} de {pages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={page >= pages}
                                            onClick={() => fetchResults(page + 1)}
                                            data-testid="stripe-next-page"
                                        >
                                            Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
